import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/theme/ThemeProvider";

import { courierPortalApi, residentPortalApi } from "@/lib/api";

import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageLoader from "@/components/PageLoader";

// Каждая страница — отдельный чанк.
// Раньше все 78 экранов лежали в одном бандле на 2.4 МБ, и курьер,
// открывший свой портал с телефона, скачивал заодно бухгалтерию,
// склад и админку. Теперь браузер берёт только тот экран, на который
// человек зашёл.
import LoginPage from "@/pages/LoginPage";
import HostelLoginPage from "@/pages/HostelLoginPage";
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AgentsCourierDetailPage = lazy(() => import("@/pages/AgentsCourierDetailPage"));
const AgentsCouriersPage = lazy(() => import("@/pages/AgentsCouriersPage"));
const AgentsDashboardPage = lazy(() => import("@/pages/AgentsDashboardPage"));
const AgentsImportPage = lazy(() => import("@/pages/AgentsImportPage"));
const AgentsLeadDetailPage = lazy(() => import("@/pages/AgentsLeadDetailPage"));
const AgentsLeadsPage = lazy(() => import("@/pages/AgentsLeadsPage"));
const AgentsPayoutsPage = lazy(() => import("@/pages/AgentsPayoutsPage"));
const AgentsRatesPage = lazy(() => import("@/pages/AgentsRatesPage"));
const AgentsSettingsPage = lazy(() => import("@/pages/AgentsSettingsPage"));
const AgentsSubmissionsPage = lazy(() => import("@/pages/AgentsSubmissionsPage"));
const AuditLogsPage = lazy(() => import("@/pages/AuditLogsPage"));
const CandidatesPage = lazy(() => import("@/pages/CandidatesPage"));
const CourierPortalHelpPage = lazy(() => import("@/pages/CourierPortalHelpPage"));
const CourierPortalLayout = lazy(() => import("@/pages/CourierPortalLayout"));
const CourierPortalLeadDetailPage = lazy(() => import("@/pages/CourierPortalLeadDetailPage"));
const CourierPortalLeadsPage = lazy(() => import("@/pages/CourierPortalLeadsPage"));
const CourierPortalLoginPage = lazy(() => import("@/pages/CourierPortalLoginPage"));
const CourierPortalPayoutsPage = lazy(() => import("@/pages/CourierPortalPayoutsPage"));
const CourierPortalRatesPage = lazy(() => import("@/pages/CourierPortalRatesPage"));
const CourierPortalRegisterPage = lazy(() => import("@/pages/CourierPortalRegisterPage"));
const CourierPortalSettingsPage = lazy(() => import("@/pages/CourierPortalSettingsPage"));
const CourierPortalSubmissionsPage = lazy(() => import("@/pages/CourierPortalSubmissionsPage"));
const CourierPortalSupportPage = lazy(() => import("@/pages/CourierPortalSupportPage"));
const DailyReportPage = lazy(() => import("@/pages/DailyReportPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const EmployeeDetailPage = lazy(() => import("@/pages/EmployeeDetailPage"));
const EmployeesPage = lazy(() => import("@/pages/EmployeesPage"));
const EnterpriseConnectionsAdminPage = lazy(() => import("@/pages/EnterpriseConnectionsAdminPage"));
const EnterprisesPage = lazy(() => import("@/pages/EnterprisesPage"));
const ExportPage = lazy(() => import("@/pages/ExportPage"));
const FinanceOverviewPage = lazy(() => import("@/pages/FinanceOverviewPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const HostelCompaniesPage = lazy(() => import("@/pages/HostelCompaniesPage"));
const HostelCounterpartiesPage = lazy(() => import("@/pages/HostelCounterpartiesPage"));
const HostelDocumentsPage = lazy(() => import("@/pages/HostelDocumentsPage"));
const HostelContractsPage = lazy(() => import("@/pages/HostelContractsPage"));
const HostelFinancePage = lazy(() => import("@/pages/HostelFinancePage"));
const HostelRegistrationPage = lazy(() => import("@/pages/HostelRegistrationPage"));
const HostelUploadPage = lazy(() => import("@/pages/HostelUploadPage"));
const HostelWarehousePage = lazy(() => import("@/pages/HostelWarehousePage"));
const HostelsPage = lazy(() => import("@/pages/HostelsPage"));
const ImportPage = lazy(() => import("@/pages/ImportPage"));
const LeadgenChannelsPage = lazy(() => import("@/pages/LeadgenChannelsPage"));
const LeadgenDashboardPage = lazy(() => import("@/pages/LeadgenDashboardPage"));
const LeadgenFormsPage = lazy(() => import("@/pages/LeadgenFormsPage"));
const LeadgenLandingsPage = lazy(() => import("@/pages/LeadgenLandingsPage"));
const LeadgenLeadsPage = lazy(() => import("@/pages/LeadgenLeadsPage"));
const LeadgenMessagesPage = lazy(() => import("@/pages/LeadgenMessagesPage"));
const LeadgenReportsPage = lazy(() => import("@/pages/LeadgenReportsPage"));
const LedgerPage = lazy(() => import("@/pages/LedgerPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const HostelMapPage = lazy(() => import("@/pages/HostelMapPage"));
const ResidentPortalLoginPage = lazy(() => import("@/pages/ResidentPortalLoginPage"));
const ResidentPortalPage = lazy(() => import("@/pages/ResidentPortalPage"));
const ManagerDashboardPage = lazy(() => import("@/pages/ManagerDashboardPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfessionsPage = lazy(() => import("@/pages/ProfessionsPage"));
const ProviderModulePage = lazy(() => import("@/pages/ProviderModulePage"));
const PublicApplyPage = lazy(() => import("@/pages/PublicApplyPage"));
const PublicLandingPage = lazy(() => import("@/pages/PublicLandingPage"));
const RentabilityPage = lazy(() => import("@/pages/RentabilityPage"));
const ResidentsSearchPage = lazy(() => import("@/pages/ResidentsSearchPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ShopCustomersPage = lazy(() => import("@/pages/ShopCustomersPage"));
const ShopLocationsPage = lazy(() => import("@/pages/ShopLocationsPage"));
const ShopPosPage = lazy(() => import("@/pages/ShopPosPage"));
const ShopProductsPage = lazy(() => import("@/pages/ShopProductsPage"));
const ShopPurchasesPage = lazy(() => import("@/pages/ShopPurchasesPage"));
const ShopReportsPage = lazy(() => import("@/pages/ShopReportsPage"));
const ShopSalesPage = lazy(() => import("@/pages/ShopSalesPage"));
const ShopSettingsPage = lazy(() => import("@/pages/ShopSettingsPage"));
const ShopShiftsPage = lazy(() => import("@/pages/ShopShiftsPage"));
const ShopStockPage = lazy(() => import("@/pages/ShopStockPage"));
const ShopSuppliersPage = lazy(() => import("@/pages/ShopSuppliersPage"));
const SubscriptionPage = lazy(() => import("@/pages/SubscriptionPage"));
const PublicPlansPage = lazy(() => import("@/pages/PublicPlansPage"));
const ProductLandingPage = lazy(() => import("@/pages/ProductLandingPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const TimesheetDetailPage = lazy(() => import("@/pages/TimesheetDetailPage"));
const UploadPage = lazy(() => import("@/pages/UploadPage"));


function Protected({ user, children }) {
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

// Полностью независимый гейт от основного user/Protected — курьерский
// портал использует свой токен (courierPortalApi.tokenKey), никак не
// связанный с логином табельщика/владельца, поэтому проверяется отдельно.
function CourierPortalProtected({ children }) {
    const hasToken = !!localStorage.getItem(courierPortalApi.tokenKey);
    if (!hasToken) return <Navigate to="/couriers/login" replace />;
    return <CourierPortalLayout>{children}</CourierPortalLayout>;
}

// Кабинет жильца проверяется отдельно: у жильца свой токен, он не
// пользователь системы и в общий Protected не попадает.
function ResidentPortalProtected({ children }) {
    const hasToken = !!localStorage.getItem(residentPortalApi.tokenKey);
    if (!hasToken) return <Navigate to="/resident/login" replace />;
    return children;
}

function App() {
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("tabel_user") || "null");
        } catch {
            return null;
        }
    });

    useEffect(() => {
        const manifest = document.getElementById("app-manifest");
        if (manifest) {
            manifest.href = window.location.pathname.startsWith("/resident")
                ? "/manifest-resident.json"
                : "/manifest-owner.json";
        }
    }, []);

    // Prevent browser from opening files dropped outside a dropzone
    useEffect(() => {
        const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
        window.addEventListener("dragover", stop);
        window.addEventListener("drop", stop);
        return () => {
            window.removeEventListener("dragover", stop);
            window.removeEventListener("drop", stop);
        };
    }, []);

    return (
        <ThemeProvider>
            <BrowserRouter>
                <Toaster
                    richColors
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: "var(--card-bg)",
                            backdropFilter: "blur(20px)",
                            border: "1px solid var(--card-border)",
                            color: "var(--foreground)",
                        },
                    }}
                />
                <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route
                        path="/login"
                        element={
                            user ? (
                                <Navigate
                                    to="/home"
                                    replace
                                />
                            ) : (
                                <LoginPage onLogin={setUser} />
                            )
                        }
                    />
                    <Route
                        path="/hostel/login"
                        element={
                            user ? (
                                <Navigate to="/home" replace />
                            ) : (
                                <HostelLoginPage setUser={setUser} />
                            )
                        }
                    />
                    {/* ===== Main Menu (Home) ===== */}
                    <Route
                        path="/home"
                        element={
                            <Protected user={user}>
                                <Layout user={user}><HomePage user={user} /></Layout>
                            </Protected>
                        }
                    />
                    <Route path="/plans" element={<PublicPlansPage />} />
                    <Route
                        path="/help"
                        element={
                            <Protected user={user}>
                                <Layout user={user}><HelpPage /></Layout>
                            </Protected>
                        }
                    />
                    <Route
                        path="/manager/dashboard"
                        element={
                            <Protected user={user}>
                                <Layout user={user}><ManagerDashboardPage /></Layout>
                            </Protected>
                        }
                    />
                    <Route
                        path="/audit"
                        element={
                            <Protected user={user}>
                                <Layout user={user}><AuditLogsPage /></Layout>
                            </Protected>
                        }
                    />
                    {/* ===== Hostel Module ===== */}
                    <Route path="/hostel/upload" element={<Protected user={user}><Layout user={user}><HostelUploadPage /></Layout></Protected>} />
                    <Route path="/hostel/finance" element={<Protected user={user}><Layout user={user}><HostelFinancePage /></Layout></Protected>} />
                    <Route path="/hostel/hostels" element={<Protected user={user}><Layout user={user}><HostelsPage /></Layout></Protected>} />
                    <Route path="/hostel/map" element={<Protected user={user}><Layout user={user}><HostelMapPage /></Layout></Protected>} />
                    <Route path="/hostel/companies" element={<Protected user={user}><Layout user={user}><HostelCompaniesPage /></Layout></Protected>} />
                    <Route path="/hostel/counterparties" element={<Protected user={user}><Layout user={user}><HostelCounterpartiesPage /></Layout></Protected>} />
                    <Route path="/hostel/warehouse" element={<Protected user={user}><Layout user={user}><HostelWarehousePage /></Layout></Protected>} />
                    <Route path="/hostel/registration" element={<Protected user={user}><Layout user={user}><HostelRegistrationPage /></Layout></Protected>} />
                    <Route path="/hostel/residents" element={<Protected user={user}><Layout user={user}><ResidentsSearchPage /></Layout></Protected>} />
                    <Route path="/hostel/documents" element={<Protected user={user}><Layout user={user}><HostelDocumentsPage /></Layout></Protected>} />
                    <Route path="/hostel/contracts" element={<Protected user={user}><Layout user={user}><HostelContractsPage /></Layout></Protected>} />
                    <Route path="/hostel/notifications" element={<Protected user={user}><Layout user={user}><NotificationsPage /></Layout></Protected>} />
                    {/* ===== Транспорт / Питание / Одежда — each its own open-able rail
                         module (like Табель/Хостел). Section list differs per category —
                         Транспорт adds "fleet" (автопарк), Одежда adds "issuance" (учёт
                         выдачи); see TRANSPORT_SUBNAV/FOOD_SUBNAV/CLOTHING_SUBNAV in
                         Layout.jsx for the matching nav entries. ===== */}
                    {Object.entries({
                        transport: ["orders", "tickets", "fleet", "reports", "pricelist", "counterparties", "finance", "connections"],
                        food: ["orders", "quota", "tickets", "reports", "pricelist", "counterparties", "finance", "connections"],
                        clothing: ["orders", "pricelist", "issuance", "counterparties", "finance", "connections"],
                    }).flatMap(([category, sections]) =>
                        sections.map((section) => (
                            <Route
                                key={`${category}-${section}`}
                                path={`/${category}/${section}`}
                                element={
                                    <Protected user={user}>
                                        <Layout user={user}>
                                            <ProviderModulePage category={category} section={section} />
                                        </Layout>
                                    </Protected>
                                }
                            />
                        ))
                    )}
                    {/* ===== Магазин (Shop) Module — "/shop/import" (Chunk 8) on hold ===== */}
                    <Route path="/shop/shops" element={<Protected user={user}><Layout user={user}><ShopLocationsPage /></Layout></Protected>} />
                    <Route path="/shop/suppliers" element={<Protected user={user}><Layout user={user}><ShopSuppliersPage /></Layout></Protected>} />
                    <Route path="/shop/customers" element={<Protected user={user}><Layout user={user}><ShopCustomersPage /></Layout></Protected>} />
                    <Route path="/shop/products" element={<Protected user={user}><Layout user={user}><ShopProductsPage /></Layout></Protected>} />
                    <Route path="/shop/stock" element={<Protected user={user}><Layout user={user}><ShopStockPage /></Layout></Protected>} />
                    <Route path="/shop/purchases" element={<Protected user={user}><Layout user={user}><ShopPurchasesPage /></Layout></Protected>} />
                    <Route path="/shop/shifts" element={<Protected user={user}><Layout user={user}><ShopShiftsPage /></Layout></Protected>} />
                    <Route path="/shop/pos" element={<Protected user={user}><Layout user={user}><ShopPosPage /></Layout></Protected>} />
                    <Route path="/shop/sales" element={<Protected user={user}><Layout user={user}><ShopSalesPage /></Layout></Protected>} />
                    <Route path="/shop/reports" element={<Protected user={user}><Layout user={user}><ShopReportsPage /></Layout></Protected>} />
                    <Route path="/shop/settings" element={<Protected user={user}><Layout user={user}><ShopSettingsPage /></Layout></Protected>} />
                    {/* ===== «Курьеры» — личный модуль, admin-сторона (внутри основного приложения) ===== */}
                    <Route path="/agents/dashboard" element={<Protected user={user}><Layout user={user}><AgentsDashboardPage /></Layout></Protected>} />
                    <Route path="/agents/leads" element={<Protected user={user}><Layout user={user}><AgentsLeadsPage /></Layout></Protected>} />
                    <Route path="/agents/leads/:externalId" element={<Protected user={user}><Layout user={user}><AgentsLeadDetailPage /></Layout></Protected>} />
                    <Route path="/agents/couriers" element={<Protected user={user}><Layout user={user}><AgentsCouriersPage /></Layout></Protected>} />
                    <Route path="/agents/couriers/:courierId" element={<Protected user={user}><Layout user={user}><AgentsCourierDetailPage /></Layout></Protected>} />
                    <Route path="/agents/payouts" element={<Protected user={user}><Layout user={user}><AgentsPayoutsPage /></Layout></Protected>} />
                    <Route path="/agents/rates" element={<Protected user={user}><Layout user={user}><AgentsRatesPage /></Layout></Protected>} />
                    <Route path="/agents/settings" element={<Protected user={user}><Layout user={user}><AgentsSettingsPage /></Layout></Protected>} />
                    <Route path="/agents/import" element={<Protected user={user}><Layout user={user}><AgentsImportPage /></Layout></Protected>} />
                    <Route path="/agents/submissions" element={<Protected user={user}><Layout user={user}><AgentsSubmissionsPage /></Layout></Protected>} />
                    {/* ===== Привлечение (лидогенерация) ===== */}
                    <Route path="/leadgen/dashboard" element={<Protected user={user}><Layout user={user}><LeadgenDashboardPage /></Layout></Protected>} />
                    <Route path="/leadgen/leads" element={<Protected user={user}><Layout user={user}><LeadgenLeadsPage /></Layout></Protected>} />
                    <Route path="/leadgen/forms" element={<Protected user={user}><Layout user={user}><LeadgenFormsPage /></Layout></Protected>} />
                    <Route path="/leadgen/landings" element={<Protected user={user}><Layout user={user}><LeadgenLandingsPage /></Layout></Protected>} />
                    <Route path="/leadgen/messages" element={<Protected user={user}><Layout user={user}><LeadgenMessagesPage /></Layout></Protected>} />
                    <Route path="/leadgen/channels" element={<Protected user={user}><Layout user={user}><LeadgenChannelsPage /></Layout></Protected>} />
                    <Route path="/leadgen/reports" element={<Protected user={user}><Layout user={user}><LeadgenReportsPage /></Layout></Protected>} />
                    {/* Публичная форма отклика — БЕЗ авторизации: её открывает
                        соискатель по ссылке/QR канала, а не пользователь системы. */}
                    {/* Кабинет жильца. Собственный токен, никак не связанный с
                        логином коменданта, — как и у курьерского портала. */}
                    <Route path="/resident/login" element={<ResidentPortalLoginPage />} />
                    <Route path="/resident" element={<ResidentPortalProtected><ResidentPortalPage /></ResidentPortalProtected>} />

                    {/* Юридические документы — открыты всем: на них ссылаются
                        форма регистрации, публичные анкеты и подвал. */}
                    <Route path="/legal/:slug" element={<LegalPage />} />
                    <Route path="/legal" element={<LegalPage />} />
                    <Route path="/apply/:slug" element={<PublicApplyPage />} />
                    <Route path="/p/:slug" element={<PublicLandingPage />} />
                    {/* ===== Портал курьеров — ОТДЕЛЬНАЯ авторизация, вне Protected/Layout ===== */}
                    <Route path="/couriers/login" element={<CourierPortalLoginPage />} />
                    <Route path="/couriers/register" element={<CourierPortalRegisterPage />} />
                    <Route path="/couriers/leads" element={<CourierPortalProtected><CourierPortalLeadsPage /></CourierPortalProtected>} />
                    <Route path="/couriers/leads/:externalId" element={<CourierPortalProtected><CourierPortalLeadDetailPage /></CourierPortalProtected>} />
                    <Route path="/couriers/submissions" element={<CourierPortalProtected><CourierPortalSubmissionsPage /></CourierPortalProtected>} />
                    <Route path="/couriers/payouts" element={<CourierPortalProtected><CourierPortalPayoutsPage /></CourierPortalProtected>} />
                    <Route path="/couriers/rates" element={<CourierPortalProtected><CourierPortalRatesPage /></CourierPortalProtected>} />
                    <Route path="/couriers/help" element={<CourierPortalProtected><CourierPortalHelpPage /></CourierPortalProtected>} />
                    <Route path="/couriers/settings" element={<CourierPortalProtected><CourierPortalSettingsPage /></CourierPortalProtected>} />
                    <Route path="/couriers/support" element={<CourierPortalProtected><CourierPortalSupportPage /></CourierPortalProtected>} />
                    {/* Public product landing for visitors; authenticated users keep the fast path to HOME. */}
                    <Route
                        path="/"
                        element={
                            user ? <Navigate to="/home" replace /> : <ProductLandingPage />
                        }
                    />
                    <Route path="/welcome" element={<ProductLandingPage />} />
                    {/* ===== Tabel Module ===== */}
                    <Route path="/dashboard" element={<Protected user={user}><Layout user={user}><DashboardPage /></Layout></Protected>} />
                    <Route path="/upload" element={<Protected user={user}><Layout user={user}><UploadPage /></Layout></Protected>} />
                    <Route path="/timesheets/:id" element={<Protected user={user}><Layout user={user}><TimesheetDetailPage /></Layout></Protected>} />
                    <Route path="/export" element={<Protected user={user}><Layout user={user}><ExportPage /></Layout></Protected>} />
                    <Route path="/employees" element={<Protected user={user}><Layout user={user}><EmployeesPage /></Layout></Protected>} />
                    <Route path="/employees/:key" element={<Protected user={user}><Layout user={user}><EmployeeDetailPage /></Layout></Protected>} />
                    <Route path="/import" element={<Protected user={user}><Layout user={user}><ImportPage /></Layout></Protected>} />
                    <Route path="/professions" element={<Protected user={user}><Layout user={user}><ProfessionsPage /></Layout></Protected>} />
                    <Route path="/enterprises" element={<Protected user={user}><Layout user={user}><EnterprisesPage /></Layout></Protected>} />
                    <Route path="/fines" element={<Protected user={user}><Layout user={user}><LedgerPage kind="fine" /></Layout></Protected>} />
                    <Route path="/deductions" element={<Protected user={user}><Layout user={user}><LedgerPage kind="deduction" /></Layout></Protected>} />
                    <Route path="/bonuses" element={<Protected user={user}><Layout user={user}><LedgerPage kind="bonus" /></Layout></Protected>} />
                    <Route path="/candidates" element={<Protected user={user}><Layout user={user}><CandidatesPage /></Layout></Protected>} />
                    <Route path="/rentability" element={<Protected user={user}><Layout user={user}><RentabilityPage /></Layout></Protected>} />
                    <Route path="/finance" element={<Protected user={user}><Layout user={user}><FinanceOverviewPage /></Layout></Protected>} />
                    <Route path="/daily-report" element={<Protected user={user}><Layout user={user}><DailyReportPage /></Layout></Protected>} />
                    <Route path="/subscription" element={<Protected user={user}><Layout user={user}><SubscriptionPage /></Layout></Protected>} />
                    <Route path="/notifications" element={<Protected user={user}><Layout user={user}><NotificationsPage /></Layout></Protected>} />
                    <Route path="/admin" element={<Protected user={user}><Layout user={user}><AdminPage /></Layout></Protected>} />
                    <Route path="/admin/enterprise-connections" element={<Protected user={user}><Layout user={user}><EnterpriseConnectionsAdminPage /></Layout></Protected>} />
                    <Route path="/settings" element={<Protected user={user}><Layout user={user}><SettingsPage /></Layout></Protected>} />
                    <Route path="/support" element={<Protected user={user}><Layout user={user}><SupportPage /></Layout></Protected>} />
                </Routes>
                </Suspense>
                </ErrorBoundary>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
